import React from 'react';
import { useTranslation } from 'react-i18next';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { IAvatarTrain, IUserModel, CreateAvatarVideoRequest } from '@packages/global';
import { useCoinStore } from '../../stores/coinStore';
import { ModelSelector } from '../common/form';
import AudioUpload from '../common/AudioUpload';
import AvatarTabs from './AvatarTabs';

interface AvatarVideoFormProps {
  videoForm: CreateAvatarVideoRequest;
  videoModels: IUserModel[];
  videoModelsLoading?: boolean;
  completedTrains: IAvatarTrain[];
  onVideoFormChange: (data: Partial<CreateAvatarVideoRequest>) => void;
  onVideoSubmit: (e: React.FormEvent) => void;
}

export default function AvatarVideoForm({
  videoForm, videoModels, videoModelsLoading, completedTrains, onVideoFormChange, onVideoSubmit,
}: AvatarVideoFormProps) {
  const { t } = useTranslation(['avatar', 'common']);
  const { account } = useCoinStore();

  const selectedModel = videoModels.find(m => m.id === videoForm.modelId);
  const coinPrice = selectedModel?.coinPrice ?? 0;

  const inputCls = 'w-full bg-bg-form border border-border-subtle rounded-lg px-3 py-2 text-gray-50 placeholder-gray-500 focus:border-border-default transition-colors outline-none';
  const labelCls = 'block text-sm text-gray-400 mb-1';

  return (
    <div className="w-80 lg:w-96 bg-bg-form border-r border-border-subtle flex flex-col overflow-hidden flex-shrink-0">
      <AvatarTabs />

      <div className="flex-1 overflow-y-auto p-5">
        <form onSubmit={onVideoSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>{t('avatar:video.model')}</label>
            <ModelSelector
              models={videoModels}
              selectedModelId={videoForm.modelId}
              onChange={modelId => onVideoFormChange({ modelId })}
              loading={videoModelsLoading}
              placeholder={t('avatar:video.selectModel')}
            />
          </div>

          <div>
            <label className={labelCls}>{t('avatar:video.title')}</label>
            <input value={videoForm.title || ''}
              onChange={e => onVideoFormChange({ title: e.target.value })}
              placeholder={t('avatar:video.titlePlaceholder')}
              className={inputCls} />
          </div>

          <div>
            <label className={labelCls}>{t('avatar:video.selectAvatar')}</label>
            <select required value={videoForm.avatarId}
              onChange={e => onVideoFormChange({ avatarId: e.target.value })}
              className={`${inputCls} cursor-pointer`}>
              <option value="">{t('avatar:video.selectAvatarPlaceholder')}</option>
              {completedTrains.map(tr => (
                <option key={tr.id} value={tr.avatarId!}>{tr.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls}>{t('avatar:video.audioUrl')}</label>
            <AudioUpload
              value={videoForm.audioUrl}
              showAudioCreator={true}
              onUploadSuccess={url => onVideoFormChange({ audioUrl: url })}
              placeholder={t('avatar:video.audioUrlPlaceholder')}
              showAudioSelector={true}
              showMusicSelector={false}
            />
          </div>

          <button type="submit"
            disabled={videoModelsLoading}
            className="w-full bg-primary hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 rounded-lg font-medium transition-colors cursor-pointer mt-2">
            {t('avatar:video.submit')}
          </button>

          {coinPrice > 0 && (
            <div className="flex items-center justify-between text-xs px-1 mt-2">
              <div className="flex items-center gap-1 text-gray-500">
                <CurrencyDollarIcon className="w-3.5 h-3.5" />
                <span>{t('avatar:video.unitPricePerSecond', { price: coinPrice })}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-500">{t('avatar:video.balance')}</span>
                <span className="font-mono text-gray-300">{account?.balance ?? 0}</span>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
